/**
 * Page Le Coffre MSC - Thème Casa de Papel
 * Design : Noir, Rouge, Blanc
 * Effets : Billets flottants, Matrix code, fond sonore immersif
 * Responsive Mobile
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Lock,
  Unlock,
  Volume2,
  VolumeX,
  Trophy,
  Gift,
  Calendar,
  Clock,
  Star,
  ChevronRight,
  Play,
  HelpCircle,
  Lightbulb,
  X,
  LogIn,
  UserPlus,
  Gamepad2,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import api from '../../lib/axios';

const MONTHS = [
  { num: 1, name: 'Jan', fullName: 'Janvier' },
  { num: 2, name: 'Fév', fullName: 'Février' },
  { num: 3, name: 'Mar', fullName: 'Mars' },
  { num: 4, name: 'Avr', fullName: 'Avril' },
  { num: 5, name: 'Mai', fullName: 'Mai' },
  { num: 6, name: 'Juin', fullName: 'Juin' },
  { num: 7, name: 'Juil', fullName: 'Juillet' },
  { num: 8, name: 'Août', fullName: 'Août' },
  { num: 9, name: 'Sep', fullName: 'Septembre' },
  { num: 10, name: 'Oct', fullName: 'Octobre' },
  { num: 11, name: 'Nov', fullName: 'Novembre' },
  { num: 12, name: 'Déc', fullName: 'Décembre' }
];

// Matrix Rain Effect Component
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Characters for the Matrix effect - combination style
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789€$£¥₿#@&%?!';
    const charArray = chars.split('');
    
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    
    // Array to track the y position of drops
    const drops = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }
    
    const draw = () => {
      // Slightly transparent black to create trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        // Random character
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        
        // Alternate between red and dark red for depth
        const brightness = Math.random();
        if (brightness > 0.95) {
          ctx.fillStyle = '#ffffff'; // Occasional white flash
        } else if (brightness > 0.7) {
          ctx.fillStyle = '#ff3333'; // Bright red
        } else {
          ctx.fillStyle = '#8b0000'; // Dark red
        }
        
        // Draw the character
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        
        // Reset drop randomly or when it goes off screen
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 50);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0 opacity-30"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

// Floating Money Bills Component - Enhanced
const FloatingBills = () => {
  const bills = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 8,
    duration: 10 + Math.random() * 10,
    size: 40 + Math.random() * 50,
    rotation: Math.random() * 360,
    opacity: 0.1 + Math.random() * 0.15
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {bills.map((bill) => (
        <div
          key={bill.id}
          className="absolute animate-float-bill"
          style={{
            left: `${bill.left}%`,
            animationDelay: `${bill.delay}s`,
            animationDuration: `${bill.duration}s`,
            opacity: bill.opacity
          }}
        >
          <svg
            width={bill.size}
            height={bill.size * 0.5}
            viewBox="0 0 100 50"
            style={{ transform: `rotate(${bill.rotation}deg)` }}
          >
            <rect x="0" y="0" width="100" height="50" rx="5" fill="#1a4a1a" stroke="#0f2f0f" strokeWidth="2" />
            <circle cx="50" cy="25" r="15" fill="none" stroke="#2a6a2a" strokeWidth="2" />
            <text x="50" y="30" textAnchor="middle" fill="#3a8a3a" fontSize="14" fontWeight="bold">€</text>
            <rect x="5" y="5" width="20" height="10" rx="2" fill="#2a6a2a" opacity="0.5" />
            <rect x="75" y="35" width="20" height="10" rx="2" fill="#2a6a2a" opacity="0.5" />
          </svg>
        </div>
      ))}
    </div>
  );
};

// Sound Control Component - Enhanced with volume indicator
const SoundControl = ({ isPlaying, onToggle, volume }) => {
  return (
    <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-black/90 backdrop-blur-md rounded-full px-4 py-2 border border-red-500/50 shadow-lg shadow-red-500/20">
      <button
        onClick={onToggle}
        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
          isPlaying 
            ? 'bg-gradient-to-br from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white shadow-lg shadow-red-500/50 scale-110' 
            : 'bg-white/10 hover:bg-white/20 text-white/70 hover:text-white'
        }`}
        data-testid="sound-toggle-btn"
      >
        {isPlaying ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
      </button>
      {!isPlaying && (
        <span className="text-white/80 text-sm font-medium animate-pulse hidden sm:block">
          🔊 Montez le volume !
        </span>
      )}
      {isPlaying && (
        <div className="hidden sm:flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-1 bg-red-500 rounded-full animate-pulse"
              style={{
                height: `${12 + Math.random() * 12}px`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Login Overlay for non-authenticated users
const LoginOverlay = ({ onLogin, onRegister }) => {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-lg rounded-2xl">
      <div className="text-center p-6 sm:p-8 max-w-md mx-4">
        <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-600 to-red-900 flex items-center justify-center border-4 border-red-400/30 shadow-2xl shadow-red-500/40 animate-pulse">
          <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
          Accès Réservé aux Membres
        </h3>
        <p className="text-white/90 mb-6 text-sm sm:text-base">
          Tentez votre chance de déverrouiller le coffre ! Inscrivez-vous pour participer et gagner le gros lot.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={onRegister}
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold px-6 py-3 shadow-lg shadow-red-500/30"
            data-testid="register-btn"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            S'inscrire
          </Button>
          <Button
            onClick={onLogin}
            variant="outline"
            className="border-white/40 text-white hover:bg-white/10 px-6 py-3"
            data-testid="login-btn"
          >
            <LogIn className="w-5 h-5 mr-2" />
            Se connecter
          </Button>
        </div>
      </div>
    </div>
  );
};

// Hint Modal
const HintModal = ({ show, monthNum, hint, onClose }) => {
  if (!show || !monthNum) return null;
  
  const monthInfo = MONTHS.find(m => m.num === monthNum);
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-slate-900 to-black border-2 border-red-500/50 rounded-2xl p-6 max-w-md w-full shadow-2xl shadow-red-500/20 relative animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-900/50 hover:bg-red-800 flex items-center justify-center text-white transition-colors"
          data-testid="close-hint-modal"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shadow-lg shadow-red-500/30">
            <Lightbulb className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white">
            Indice de {monthInfo?.fullName}
          </h3>
          <p className="text-sm text-red-400 mt-1">
            Caractère mystère #{monthNum}
          </p>
        </div>
        
        {hint ? (
          <div className="bg-black/50 border border-red-500/30 rounded-xl p-4">
            <p className="text-white text-center text-lg italic">
              « {hint} »
            </p>
          </div>
        ) : (
          <div className="text-center text-white/50">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Indice non disponible</p>
          </div>
        )}
        
        <div className="mt-6 text-center">
          <Button onClick={onClose} className="bg-red-600 hover:bg-red-700">
            Compris !
          </Button>
        </div>
      </div>
    </div>
  );
};

// Coffre Image Component with glow effect
const CoffreImage = () => {
  return (
    <div className="relative group">
      {/* Glow effect behind */}
      <div className="absolute inset-0 bg-red-600/30 rounded-full blur-3xl scale-150 group-hover:scale-175 transition-transform duration-700" />
      
      {/* Main vault image */}
      <div className="relative w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/8dc15090-04bb-47d6-96fa-05b7e2f22442/images/058d4c2372c7aef375491f9ceed35bc098d0b1753e8dfb0dc96bd76ef580d664.png" 
          alt="Le Coffre MSC" 
          className="w-full h-full object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-105"
          style={{ filter: 'drop-shadow(0 0 40px rgba(220, 38, 38, 0.6))' }}
        />
        
        {/* Animated ring around vault */}
        <div className="absolute inset-0 rounded-full border-4 border-red-500/30 animate-ping-slow" />
        <div className="absolute inset-4 rounded-full border-2 border-red-400/20 animate-spin-slow" />
      </div>
    </div>
  );
};

// Main Component
const CoffreMSCPage = () => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [player, setPlayer] = useState(null);
  const [annualGame, setAnnualGame] = useState(null);
  const [periodicGames, setPeriodicGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundPlaying, setSoundPlaying] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [selectedHintMonth, setSelectedHintMonth] = useState(null);
  const [monthlyCountdown, setMonthlyCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // Countdown timer for end of current month quiz
  useEffect(() => {
    const calculateMonthlyCountdown = () => {
      const now = new Date();
      const lastDayOfMonth = new Date(currentYear, currentMonth, 0, 23, 59, 59);
      const diff = lastDayOfMonth - now;
      
      if (diff <= 0) {
        setMonthlyCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setMonthlyCountdown({ days, hours, minutes, seconds });
    };
    
    calculateMonthlyCountdown();
    const interval = setInterval(calculateMonthlyCountdown, 1000);
    return () => clearInterval(interval);
  }, [currentYear, currentMonth]);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('player_token');
    const playerData = localStorage.getItem('player_data');
    if (token && playerData) {
      setIsAuthenticated(true);
      setPlayer(JSON.parse(playerData));
    }
    
    fetchData();
  }, []);

  // Auto-play audio on page load
  useEffect(() => {
    const attemptAutoplay = async () => {
      if (audioRef.current) {
        audioRef.current.volume = 0.5;
        try {
          await audioRef.current.play();
          setSoundPlaying(true);
        } catch (e) {
          // Autoplay blocked by browser - user needs to click to enable
          console.log('Autoplay blocked, waiting for user interaction');
          setSoundPlaying(false);
        }
      }
    };
    
    // Small delay to ensure audio element is ready
    const timer = setTimeout(attemptAutoplay, 500);
    return () => clearTimeout(timer);
  }, [loading]); // Run after loading completes

  const fetchData = async () => {
    try {
      // Track page visit
      api.post('/game/tracking/page-visit', { page: 'coffre-msc', referrer: document.referrer }).catch(() => {});
      
      // Fetch annual game (note: singular "annual-game" not "annual-games")
      const annualRes = await api.get(`/game/annual-game/${currentYear}`);
      setAnnualGame(annualRes.data);
      
      // Fetch periodic games (monthly quizzes)
      const periodicRes = await api.get(`/game/periodic-games/${currentYear}`);
      setPeriodicGames(periodicRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSound = useCallback(() => {
    if (audioRef.current) {
      if (soundPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.volume = 0.6;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
      setSoundPlaying(!soundPlaying);
    }
  }, [soundPlaying]);

  const handleShowHint = (month) => {
    setSelectedHintMonth(month);
    setShowHintModal(true);
  };

  const getHintForMonth = (month) => {
    return annualGame?.secret_hints?.find(h => h.month === month)?.hint || null;
  };

  const getQuizForMonth = (month) => {
    return periodicGames.find(g => g.month === month);
  };

  // Calculate days until Christmas (coffre opening)
  const daysUntilOpening = () => {
    const now = new Date();
    const christmas = new Date(currentYear, 11, 24); // December 24
    if (now > christmas) return 0;
    const diff = christmas - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70 text-lg">Chargement du coffre...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="coffre-msc-page">
      {/* Background Effects Layer */}
      <div className="fixed inset-0 z-0">
        {/* Matrix Rain Effect */}
        <MatrixRain />
        
        {/* Floating Bills */}
        <FloatingBills />
        
        {/* Red gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/30 via-black/50 to-red-900/40 pointer-events-none" />
        
        {/* Vignette effect */}
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" 
          style={{ background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.8) 100%)' }} 
        />
      </div>
      
      {/* Audio Element - Theme music from config or fallback */}
      <audio
        ref={audioRef}
        loop
        preload="auto"
        src={annualGame?.theme_audio_url || "https://assets.mixkit.co/music/preview/mixkit-driving-ambition-32.mp3"}
      />
      
      {/* Sound Control */}
      <SoundControl isPlaying={soundPlaying} onToggle={toggleSound} />
      
      {/* Navigation buttons - Top Left */}
      <div className="fixed top-4 left-4 z-50 flex items-center gap-2">
        <Link 
          to="/" 
          className="flex items-center gap-2 bg-black/90 backdrop-blur-md rounded-full px-4 py-2.5 border border-red-500/50 shadow-lg shadow-red-500/20 text-white hover:bg-red-900/50 transition-all duration-300 group"
          data-testid="back-to-site-btn"
        >
          <ChevronRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium hidden sm:inline">Retour au site</span>
        </Link>
        
        <Link 
          to="/mon-espace-joueur" 
          className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 backdrop-blur-md rounded-full px-4 py-2.5 border border-red-500/50 shadow-lg shadow-red-500/30 text-white hover:from-red-500 hover:to-red-600 transition-all duration-300"
          data-testid="player-space-btn"
        >
          <Trophy className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Mon Espace</span>
        </Link>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-6 sm:py-8">
        {/* Header with Coffre Image */}
        <header className="text-center mb-8 sm:mb-12 pt-12 sm:pt-16">
          {/* Coffre Image - Centered */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <CoffreImage />
          </div>
          
          <div className="inline-block mb-4">
            <Badge className="bg-gradient-to-r from-red-600 to-red-700 text-white text-sm px-4 py-1.5 shadow-lg shadow-red-500/30">
              🎭 Édition {currentYear}
            </Badge>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-4 tracking-tight">
            <span className="text-white drop-shadow-lg">LE</span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600 drop-shadow-lg">COFFRE</span>{' '}
            <span className="text-white drop-shadow-lg">MSC</span>
          </h1>
          
          <p className="text-base sm:text-xl text-white/80 max-w-2xl mx-auto mb-4 px-4">
            12 mois. 12 indices. 1 combinaison secrète à découvrir.
            <br />
            <span className="text-red-400 font-semibold">Serez-vous celui qui ouvrira le coffre ?</span>
          </p>
          
          {/* Legal notice */}
          <p className="text-white/50 text-xs sm:text-sm mb-6 px-4">
            🎯 Jeu sans obligation d'achat • Réservé aux professionnels
          </p>
          
          {/* Auth buttons - Moved to top */}
          {!isAuthenticated && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <Button
                onClick={() => {
                  const currentQuiz = periodicGames.find(g => g.month === currentMonth);
                  if (currentQuiz?.id) {
                    navigate(`/jeu/${currentQuiz.id}/inscription`);
                  } else if (periodicGames.length > 0 && periodicGames[0]?.id) {
                    navigate(`/jeu/${periodicGames[0].id}/inscription`);
                  } else {
                    navigate('/jeux-concours');
                  }
                }}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold px-8 py-3 text-lg shadow-lg shadow-red-500/30"
                data-testid="header-register-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                S'inscrire
              </Button>
              <Button
                onClick={() => {
                  const currentQuiz = periodicGames.find(g => g.month === currentMonth);
                  if (currentQuiz?.id) {
                    navigate(`/jeu/${currentQuiz.id}/connexion`);
                  } else if (periodicGames.length > 0 && periodicGames[0]?.id) {
                    navigate(`/jeu/${periodicGames[0].id}/connexion`);
                  } else {
                    navigate('/jeux-concours');
                  }
                }}
                variant="outline"
                className="border-white/40 text-white hover:bg-white/10 px-8 py-3 text-lg"
                data-testid="header-login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Se connecter
              </Button>
            </div>
          )}
        </header>

        {/* Prize Section - Enhanced */}
        <section className="mb-8 sm:mb-12">
          <Card className="bg-gradient-to-br from-red-900/50 to-black/80 border-2 border-red-500/40 overflow-hidden backdrop-blur-sm shadow-2xl shadow-red-500/20">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col items-center text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3 text-white">
                  <Gift className="w-7 h-7 sm:w-8 sm:h-8 text-red-500" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-red-500">Le Gros Lot</span>
                </h2>
                <p className="text-xl sm:text-2xl font-semibold text-white mb-2">
                  {annualGame?.prize || 'À découvrir'}
                </p>
                {annualGame?.prize_value && (
                  <p className="text-red-400 text-lg sm:text-xl font-medium">
                    Valeur : {annualGame.prize_value}€ HT
                  </p>
                )}
                {annualGame?.prize_description && (
                  <p className="text-white/60 mt-3 max-w-xl text-sm sm:text-base">
                    {annualGame.prize_description}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Monthly Quizzes Grid */}
        <section className="mb-8 sm:mb-12 relative">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-3 text-white">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
            Les 12 Quiz Mensuels
          </h2>
          
          {/* Button to play current month quiz */}
          {(() => {
            const currentQuiz = periodicGames.find(g => g.month === currentMonth);
            if (currentQuiz) {
              return (
                <div className="mb-6 flex justify-center">
                  <Button
                    onClick={() => navigate(`/jeu/${currentQuiz.id}`)}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-bold px-8 py-4 text-lg shadow-lg shadow-amber-500/30 animate-pulse"
                    data-testid="play-current-quiz-btn"
                  >
                    <Play className="w-6 h-6 mr-2" />
                    Jouer au Quiz de {MONTHS[currentMonth - 1]?.fullName}
                  </Button>
                </div>
              );
            }
            return null;
          })()}
          
          {/* Monthly Quiz Countdown */}
          <div className="mb-6 p-4 sm:p-5 rounded-xl bg-gradient-to-r from-red-900/60 to-red-800/40 border-2 border-red-500/50 backdrop-blur-sm">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-red-300 text-sm font-medium mb-1">⏱️ Temps restant pour jouer au quiz de</p>
                <p className="text-white text-xl sm:text-2xl font-bold">{MONTHS[currentMonth - 1]?.fullName} et obtenir votre caractère</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="text-center bg-black/40 rounded-lg px-3 py-2 min-w-[60px]">
                  <span className="text-2xl sm:text-3xl font-bold text-white">{monthlyCountdown.days}</span>
                  <p className="text-xs text-red-300">jours</p>
                </div>
                <span className="text-red-500 text-2xl font-bold">:</span>
                <div className="text-center bg-black/40 rounded-lg px-3 py-2 min-w-[60px]">
                  <span className="text-2xl sm:text-3xl font-bold text-white">{String(monthlyCountdown.hours).padStart(2, '0')}</span>
                  <p className="text-xs text-red-300">heures</p>
                </div>
                <span className="text-red-500 text-2xl font-bold">:</span>
                <div className="text-center bg-black/40 rounded-lg px-3 py-2 min-w-[60px]">
                  <span className="text-2xl sm:text-3xl font-bold text-white">{String(monthlyCountdown.minutes).padStart(2, '0')}</span>
                  <p className="text-xs text-red-300">min</p>
                </div>
                <span className="text-red-500 text-2xl font-bold animate-pulse">:</span>
                <div className="text-center bg-black/40 rounded-lg px-3 py-2 min-w-[60px]">
                  <span className="text-2xl sm:text-3xl font-bold text-red-400 animate-pulse">{String(monthlyCountdown.seconds).padStart(2, '0')}</span>
                  <p className="text-xs text-red-300">sec</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Current Month Hint - Highlighted */}
          {getHintForMonth(currentMonth) && (
            <div className="mb-6 p-5 sm:p-6 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 border-2 border-amber-500/50 backdrop-blur-sm relative overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-500/20 rounded-full blur-2xl" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">Indice de {MONTHS[currentMonth - 1]?.fullName}</h3>
                    <p className="text-amber-300 text-sm">Caractère mystère #{currentMonth}</p>
                  </div>
                </div>
                
                <div className="bg-black/40 rounded-xl p-4 border border-amber-500/30">
                  <p className="text-white text-lg sm:text-xl text-center italic font-medium">
                    « {getHintForMonth(currentMonth)} »
                  </p>
                </div>
                
                <p className="text-amber-200/70 text-xs sm:text-sm mt-3 text-center">
                  💡 Utilisez cet indice pour trouver le caractère secret de ce mois !
                </p>
              </div>
            </div>
          )}
          
          <div className={`relative ${!isAuthenticated ? 'select-none' : ''}`}>
            {/* Blur overlay for non-authenticated */}
            {!isAuthenticated && (
              <LoginOverlay 
                onLogin={() => {
                  // Use current month quiz ID
                  const currentQuiz = periodicGames.find(g => g.month === currentMonth);
                  if (currentQuiz?.id) {
                    navigate(`/jeu/${currentQuiz.id}/connexion`);
                  } else if (periodicGames.length > 0 && periodicGames[0]?.id) {
                    navigate(`/jeu/${periodicGames[0].id}/connexion`);
                  } else {
                    // Fallback - go to games list with a message
                    navigate('/jeux-concours', { state: { message: 'Veuillez choisir un jeu pour vous connecter' } });
                  }
                }}
                onRegister={() => {
                  const currentQuiz = periodicGames.find(g => g.month === currentMonth);
                  if (currentQuiz?.id) {
                    navigate(`/jeu/${currentQuiz.id}/inscription`);
                  } else if (periodicGames.length > 0 && periodicGames[0]?.id) {
                    navigate(`/jeu/${periodicGames[0].id}/inscription`);
                  } else {
                    navigate('/jeux-concours', { state: { message: 'Veuillez choisir un jeu pour vous inscrire' } });
                  }
                }}
              />
            )}
            
            <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 ${!isAuthenticated ? 'blur-md' : ''}`}>
              {MONTHS.map((month) => {
                const quiz = getQuizForMonth(month.num);
                const hint = getHintForMonth(month.num);
                const isCurrentMonth = month.num === currentMonth;
                const isPast = month.num < currentMonth;
                const isFuture = month.num > currentMonth;
                
                return (
                  <div
                    key={month.num}
                    className={`
                      relative p-3 sm:p-4 rounded-xl border-2 transition-all duration-300
                      ${isCurrentMonth 
                        ? 'bg-red-900/50 border-red-500 shadow-lg shadow-red-500/30' 
                        : isPast
                          ? 'bg-white/5 border-white/20 hover:border-white/40'
                          : 'bg-black/40 border-white/10 opacity-60'
                      }
                    `}
                    data-testid={`month-card-${month.num}`}
                  >
                    {/* Month Header */}
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <span className={`font-bold text-sm sm:text-base ${isCurrentMonth ? 'text-red-400' : 'text-white/70'}`}>
                        {month.fullName}
                      </span>
                      {isCurrentMonth && (
                        <Badge className="bg-red-600 text-white text-xs px-1.5 py-0.5">
                          EN COURS
                        </Badge>
                      )}
                      {isPast && quiz && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                    
                    {/* Character Display */}
                    <div className="text-center mb-2 sm:mb-3">
                      <div className={`
                        w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-lg flex items-center justify-center font-mono text-xl sm:text-2xl font-bold
                        ${isPast || isCurrentMonth
                          ? 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg shadow-red-500/30'
                          : 'bg-white/10 text-white/30'
                        }
                      `}>
                        {isPast ? '?' : isFuture ? <Lock className="w-4 h-4 sm:w-5 sm:h-5" /> : '?'}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-1 sm:gap-2">
                      {hint && (isPast || isCurrentMonth) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flex-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/30 px-1 sm:px-2"
                          onClick={() => handleShowHint(month.num)}
                          data-testid={`hint-btn-${month.num}`}
                        >
                          <Lightbulb className="w-3 h-3 mr-1" />
                          <span className="hidden sm:inline">Indice</span>
                        </Button>
                      )}
                      {quiz && (isPast || isCurrentMonth) && (
                        <Button
                          size="sm"
                          className="flex-1 text-xs bg-red-600 hover:bg-red-700 px-1 sm:px-2"
                          onClick={() => navigate(`/jeu/${quiz.id}`)}
                          data-testid={`play-btn-${month.num}`}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          <span className="hidden sm:inline">Jouer</span>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Combination Display - Enhanced */}
        <section className="mb-8 sm:mb-12">
          <Card className="bg-black/70 border-2 border-red-500/40 backdrop-blur-sm">
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold mb-2 flex items-center gap-2 text-white">
                <Lock className="w-5 h-5 text-red-500" />
                La Combinaison Secrète
              </h3>
              <p className="text-red-300 text-xs sm:text-sm mb-4 text-center">
                ⚠️ Attention : Les caractères ne sont PAS dans l'ordre des mois ! Les positions sont attribuées aléatoirement.
              </p>
              <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
                {Array.from({ length: 24 }, (_, i) => {
                  // Note: La position réelle dans la combinaison est aléatoire
                  // On affiche simplement 24 cases sans lien direct avec les mois
                  const position = i + 1;
                  const isQuizSlot = i % 2 === 0; // Alternance visuelle quiz/indice
                  
                  return (
                    <div
                      key={i}
                      className={`
                        w-8 h-10 sm:w-10 sm:h-12 rounded-lg flex items-center justify-center font-mono text-sm sm:text-lg font-bold
                        transition-all duration-300 relative group
                        ${isQuizSlot 
                          ? 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-md shadow-red-500/30' 
                          : 'bg-white text-black shadow-md'
                        }
                      `}
                      title={`Position ${position}`}
                    >
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-black/80 rounded-full text-[8px] text-white/60 flex items-center justify-center">
                        {position}
                      </span>
                      ?
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-center space-y-1">
                <p className="text-white/70 text-xs sm:text-sm">
                  24 caractères à découvrir • <span className="text-red-400">Quiz (rouge)</span> et <span className="text-white">Indices (blanc)</span>
                </p>
                <p className="text-red-300/60 text-xs">
                  🎲 Les positions sont mélangées - devinez l'ordre pour ouvrir le coffre !
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* How to Play - Enhanced */}
        <section className="mb-8 sm:mb-12">
          <Card className="bg-gradient-to-br from-red-900/30 to-black/50 border border-red-500/30 backdrop-blur-sm">
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center text-white">Comment Jouer ?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center p-4 rounded-xl bg-black/40 border border-red-500/20">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shadow-lg shadow-red-500/30">
                    <span className="text-xl sm:text-2xl font-bold text-white">1</span>
                  </div>
                  <h4 className="font-bold mb-2 text-sm sm:text-base text-white">Inscrivez-vous</h4>
                  <p className="text-white/80 text-xs sm:text-sm">Créez votre compte joueur pour participer</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-black/40 border border-red-500/20">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shadow-lg shadow-red-500/30">
                    <span className="text-xl sm:text-2xl font-bold text-white">2</span>
                  </div>
                  <h4 className="font-bold mb-2 text-sm sm:text-base text-white">Jouez chaque mois</h4>
                  <p className="text-white/80 text-xs sm:text-sm">Répondez aux quiz et découvrez les indices</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-black/40 border border-red-500/20">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shadow-lg shadow-red-500/30">
                    <span className="text-xl sm:text-2xl font-bold text-white">3</span>
                  </div>
                  <h4 className="font-bold mb-2 text-sm sm:text-base text-white">Ouvrez le coffre</h4>
                  <p className="text-white/80 text-xs sm:text-sm">Trouvez la combinaison et remportez le lot !</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Coffre Opening Countdown - Moved to bottom */}
        <section className="mb-8 sm:mb-12">
          <Card className="bg-gradient-to-br from-black/80 to-red-950/50 border-2 border-red-500/50 overflow-hidden backdrop-blur-sm shadow-2xl shadow-red-500/20">
            <CardContent className="p-6 sm:p-8">
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                  <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-red-500" />
                  Ouverture du Coffre
                </h3>
                <p className="text-white/60 text-sm sm:text-base mb-4">
                  Le 24 décembre {currentYear}, le coffre s'ouvrira et le gagnant sera révélé !
                </p>
                <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-black/60 backdrop-blur-md border-2 border-red-500/40 rounded-2xl px-6 py-4 shadow-xl shadow-red-500/10">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 animate-pulse" />
                    <span className="text-white/80 text-sm sm:text-base">Plus que</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-red-600">
                      {daysUntilOpening()}
                    </span>
                    <span className="text-white/80 text-sm sm:text-base">jours</span>
                  </div>
                  <span className="text-white/60 text-sm">avant l'ouverture</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Footer - Link to other games */}
        <footer className="text-center py-6 sm:py-8 border-t border-white/10">
          <p className="text-white/50 mb-4 text-sm sm:text-base">Découvrez aussi nos autres jeux-concours</p>
          <Link to="/jeux-concours">
            <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500">
              <Gamepad2 className="w-5 h-5 mr-2" />
              Voir les Jeux Ponctuels
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </footer>
      </div>

      {/* Hint Modal */}
      <HintModal
        show={showHintModal}
        monthNum={selectedHintMonth}
        hint={getHintForMonth(selectedHintMonth)}
        onClose={() => setShowHintModal(false)}
      />
      
      {/* Custom CSS Animations */}
      <style>{`
        @keyframes ping-slow {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.1; }
        }
        .animate-ping-slow {
          animation: ping-slow 3s ease-in-out infinite;
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        
        @keyframes float-bill {
          0% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.2;
          }
          90% {
            opacity: 0.2;
          }
          100% {
            transform: translateY(-100px) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-float-bill {
          animation: float-bill linear infinite;
        }
      `}</style>
    </div>
  );
};

export default CoffreMSCPage;
